import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Paperclip, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export default function ChatCard({ card, users, me, onData }) {
  const qc = useQueryClient();
  const d = card.data || {};
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const { data: channels = [] } = useQuery({
    queryKey: ["channels"],
    queryFn: () => api.get("/channels").then((r) => r.data),
  });

  const channelId = d.channel_id || channels[0]?.id;

  const { data: messages = [] } = useQuery({
    queryKey: ["messages", channelId],
    queryFn: () => api.get(`/channels/${channelId}/messages`).then((r) => r.data),
    enabled: !!channelId,
    refetchInterval: 15000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const selectChannel = async (val) => {
    if (val.startsWith("dm:")) {
      try {
        const { data } = await api.post("/channels/dm", { user_id: val.slice(3) });
        qc.invalidateQueries({ queryKey: ["channels"] });
        onData({ channel_id: data.id });
      } catch (e) { toast.error(apiError(e)); }
    } else if (val === "new-group") {
      const name = window.prompt("Group channel name?");
      if (!name) return;
      try {
        const { data } = await api.post("/channels", { name, member_ids: users.map((u) => u.id) });
        qc.invalidateQueries({ queryKey: ["channels"] });
        onData({ channel_id: data.id });
      } catch (e) { toast.error(apiError(e)); }
    } else {
      onData({ channel_id: val });
    }
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Max file size 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, type: file.type, data: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const send = async () => {
    if (!text.trim() && !attachment) return;
    try {
      await api.post(`/channels/${channelId}/messages`, { text, attachment });
      setText(""); setAttachment(null);
      qc.invalidateQueries({ queryKey: ["messages", channelId] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <select data-testid="chat-channel-select" value={channelId || ""} onChange={(e) => selectChannel(e.target.value)}
          className="flex-1 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1.5 text-xs outline-none cursor-pointer">
          {channels.map((c) => (
            <option key={c.id} value={c.id}>{c.is_dm ? `💬 ${c.name}` : `# ${c.name}`}</option>
          ))}
          <optgroup label="Start a DM">
            {users.filter((u) => u.id !== me.id).map((u) => (
              <option key={u.id} value={`dm:${u.id}`}>{`DM · ${u.name}`}</option>
            ))}
          </optgroup>
          <option value="new-group">＋ New group channel</option>
        </select>
      </div>

      <div className="space-y-2 overflow-y-auto rounded-xl bg-white/40 dark:bg-black/25 p-2" data-testid="chat-messages"
        style={{ height: card?.h ? Math.max(150, card.h - 170) : 224 }}>
        {messages.length === 0 && <p className="pt-16 text-center font-hand text-xl opacity-50">no messages yet... say hi ✦</p>}
        {messages.map((m) => {
          const mine = m.sender_id === me.id;
          const isImage = m.attachment?.type?.startsWith("image/");
          return (
            <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${
                mine ? "bg-violet-500 text-white rounded-br-md" : "bg-white dark:bg-slate-700 dark:text-slate-100 rounded-bl-md"
              }`}>
                {!mine && <div className="text-[10px] font-bold opacity-60">{m.sender_name}</div>}
                {m.attachment && (isImage ? (
                  <img src={m.attachment.data} alt={m.attachment.name} className="my-1 max-h-36 rounded-lg" />
                ) : (
                  <a href={m.attachment.data} download={m.attachment.name}
                    className="my-1 flex items-center gap-1.5 rounded-lg bg-black/15 px-2 py-1.5 text-xs underline">
                    <FileText className="h-3.5 w-3.5" /> {m.attachment.name}
                  </a>
                ))}
                {m.text && <div>{m.text}</div>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {attachment && (
        <div className="flex items-center gap-1.5 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1 text-xs">
          <Paperclip className="h-3 w-3" /> {attachment.name}
          <button onClick={() => setAttachment(null)} className="ml-auto font-bold hover:text-red-500">✕</button>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input ref={fileRef} type="file" hidden onChange={pickFile} data-testid="chat-file-input" />
        <button data-testid="chat-attach-button" onClick={() => fileRef.current?.click()}
          className="rounded-xl bg-black/10 dark:bg-white/10 p-2 transition-colors hover:bg-black/20">
          <Paperclip className="h-4 w-4" />
        </button>
        <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message..."
          className="flex-1 rounded-xl bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none placeholder:opacity-50" />
        <button data-testid="chat-send-button" onClick={send}
          className="rounded-xl bg-violet-500 p-2 text-white shadow transition-transform hover:scale-105 active:scale-95">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
